import React, { useEffect, useMemo, useState } from "react";
import { executeMutation, executeQuery } from "../../../graphql/client";

// Invites tab: the ladies-paywall control room.
//   Left/top — Instagram contest entries (/win): the handles to
//   evaluate. One tap opens the profile; "Free code" / "Discount code"
//   mints a code tied to that entry and hands you the DM text to paste.
//   Bottom — every code ever issued, with redemption status.
// Codes are deliberately case-by-case: nothing here is bulk.

const chip = (bg, color, border) => ({
  display: "inline-block",
  background: bg,
  color,
  border: `1px solid ${border}`,
  borderRadius: 999,
  padding: "1px 8px",
  fontSize: 11,
  fontWeight: 700,
  marginLeft: 6,
});

const STATUS_CHIP = {
  new: chip("#fff6de", "#8a5a00", "#e8c56b"),
  invited: chip("#e4f5e9", "#1f7a3a", "#a5d6a7"),
  declined: chip("#f1f3f6", "#6a7385", "#d0d6e0"),
};

const btn = (primary) => ({
  padding: "5px 10px",
  background: primary ? "#1f7a3a" : "#fff",
  color: primary ? "#fff" : "#1f7a3a",
  border: "1px solid #1f7a3a",
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  marginRight: 6,
});

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const CODE_FIELDS = `code kind discountPct note igHandle email createdAt createdBy expiresAt maxUses uses redeemedBy lastRedeemedAt link`;

const dmText = (code) =>
  code.kind === "free"
    ? `Hey! We'd love to have you in Active Beaches 🌴 Your membership is on us — sign up here and it applies automatically: ${code.link}  (code ${code.code} if it asks)`
    : `Hey! We'd love to have you in Active Beaches 🌴 Here's ${code.discountPct}% off — sign up here and it applies automatically: ${code.link}  (code ${code.code} if it asks)`;

const InvitesTab = () => {
  const [entries, setEntries] = useState([]);
  const [codes, setCodes] = useState([]);
  const [filter, setFilter] = useState("new");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [copied, setCopied] = useState("");
  // Stand-alone code form (someone who didn't come through /win)
  const [manual, setManual] = useState({ igHandle: "", email: "", note: "", kind: "free", discountPct: 50 });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const out = await executeQuery(`query Invites {
        igEntries { id igHandle igUrl email phone sharedPost refCode createdAt status inviteCode }
        inviteCodes { ${CODE_FIELDS} }
      }`);
      setEntries(out?.igEntries || []);
      setCodes(out?.inviteCodes || []);
    } catch (err) {
      setError(err.message || "Couldn't load invites.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 2000);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.prompt("Copy:", text);
    }
  };

  const mintCode = async ({ kind, discountPct, igHandle, email, note, entry }) => {
    setBusy(entry ? entry.id : "manual");
    setError("");
    try {
      const out = await executeMutation(
        `mutation CreateInviteCode($input: InviteCodeInput!) {
          createInviteCode(input: $input) { ${CODE_FIELDS} }
        }`,
        { input: { kind, discountPct: kind === "discount" ? Number(discountPct) : null, igHandle, email, note, expiresDays: 30, maxUses: 1 } }
      );
      const code = out.createInviteCode;
      if (entry) {
        await executeMutation(
          `mutation SetIgEntryStatus($id: ID!, $status: String!, $inviteCode: String) {
            setIgEntryStatus(id: $id, status: $status, inviteCode: $inviteCode) { id status }
          }`,
          { id: entry.id, status: "invited", inviteCode: code.code }
        );
      }
      await copy(dmText(code), `code-${code.code}`);
      await load();
    } catch (err) {
      setError(err.message || "Couldn't create the code.");
    } finally {
      setBusy("");
    }
  };

  const setStatus = async (entry, status) => {
    setBusy(entry.id);
    try {
      await executeMutation(
        `mutation SetIgEntryStatus($id: ID!, $status: String!) {
          setIgEntryStatus(id: $id, status: $status) { id status }
        }`,
        { id: entry.id, status }
      );
      await load();
    } catch (err) {
      setError(err.message || "Couldn't update.");
    } finally {
      setBusy("");
    }
  };

  const shown = useMemo(
    () => entries.filter((e) => (filter === "all" ? true : e.status === filter)),
    [entries, filter]
  );
  const counts = useMemo(() => {
    const c = { new: 0, invited: 0, declined: 0 };
    entries.forEach((e) => { if (c[e.status] != null) c[e.status] += 1; });
    return c;
  }, [entries]);

  return (
    <div style={{ padding: "8px 4px" }}>
      <h3 style={{ marginBottom: 4 }}>🎟️ Invites</h3>
      <p style={{ fontSize: 13, color: "#5b667a", marginTop: 0 }}>
        Entries from <code>activebeaches.com/win</code> arrive here. Open the profile,
        decide, mint a code — the DM text lands on your clipboard. Codes are single-use
        and expire in 30 days. The <strong>ladiesPaywall</strong> flag (Flags tab) controls
        whether new women see the paywall at all.
      </p>
      {error && <div style={{ color: "#b02a2a", marginBottom: 8 }}>{error}</div>}

      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        {["new", "invited", "declined", "all"].map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            style={{ ...btn(filter === f), marginRight: 0 }}
          >
            {f === "all" ? "All" : f[0].toUpperCase() + f.slice(1)}
            {f !== "all" ? ` (${counts[f]})` : ` (${entries.length})`}
          </button>
        ))}
        <button type="button" onClick={load} style={{ ...btn(false), marginLeft: "auto" }}>
          ↻ Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ color: "#5b667a" }}>Loading…</div>
      ) : shown.length === 0 ? (
        <div style={{ color: "#5b667a", fontSize: 14, marginBottom: 16 }}>
          No {filter === "all" ? "" : filter} entries.
        </div>
      ) : (
        <div style={{ overflowX: "auto", marginBottom: 20 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#5b667a", fontSize: 12 }}>
                <th style={{ padding: 6 }}>Instagram</th>
                <th style={{ padding: 6 }}>Email / phone</th>
                <th style={{ padding: 6 }}>Entered</th>
                <th style={{ padding: 6 }}>Status</th>
                <th style={{ padding: 6 }} />
              </tr>
            </thead>
            <tbody>
              {shown.map((e) => (
                <tr key={e.id} style={{ borderTop: "1px solid #eef1f6" }}>
                  <td style={{ padding: 6 }}>
                    <a href={e.igUrl} target="_blank" rel="noreferrer" style={{ fontWeight: 700 }}>
                      @{e.igHandle}
                    </a>
                    {e.sharedPost && <span style={chip("#eef4fb", "#1d3d5f", "#bcd4ef")}>shared</span>}
                    {e.refCode && <span style={chip("#f1f3f6", "#6a7385", "#d0d6e0")}>ref {e.refCode}</span>}
                  </td>
                  <td style={{ padding: 6 }}>
                    {e.email}
                    {e.phone && <div style={{ color: "#5b667a" }}>{e.phone}</div>}
                  </td>
                  <td style={{ padding: 6, whiteSpace: "nowrap" }}>{fmt(e.createdAt)}</td>
                  <td style={{ padding: 6 }}>
                    <span style={STATUS_CHIP[e.status] || STATUS_CHIP.new}>{e.status}</span>
                    {e.inviteCode && <div style={{ fontSize: 11, color: "#5b667a" }}>code {e.inviteCode}</div>}
                  </td>
                  <td style={{ padding: 6, whiteSpace: "nowrap" }}>
                    {e.status !== "invited" && (
                      <>
                        <button
                          type="button"
                          disabled={busy === e.id}
                          onClick={() => mintCode({ kind: "free", igHandle: e.igHandle, email: e.email, note: `IG entry ${e.id}`, entry: e })}
                          style={btn(true)}
                        >
                          Free code
                        </button>
                        <button
                          type="button"
                          disabled={busy === e.id}
                          onClick={() => mintCode({ kind: "discount", discountPct: 50, igHandle: e.igHandle, email: e.email, note: `IG entry ${e.id}`, entry: e })}
                          style={btn(false)}
                        >
                          50% code
                        </button>
                      </>
                    )}
                    {e.status !== "declined" && (
                      <button type="button" disabled={busy === e.id} onClick={() => setStatus(e, "declined")} style={{ ...btn(false), color: "#6a7385", borderColor: "#d0d6e0" }}>
                        Decline
                      </button>
                    )}
                    {e.status !== "new" && (
                      <button type="button" disabled={busy === e.id} onClick={() => setStatus(e, "new")} style={{ ...btn(false), color: "#6a7385", borderColor: "#d0d6e0" }}>
                        Reopen
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ background: "#f7f8fa", border: "1px solid #e3e7ef", borderRadius: 8, padding: 12, marginBottom: 20 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Mint a code for someone else</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input placeholder="@instagram (optional)" value={manual.igHandle} onChange={(e) => setManual({ ...manual, igHandle: e.target.value })} style={{ padding: 6, border: "1px solid #c9d0dc", borderRadius: 6, width: 170 }} />
          <input placeholder="email (optional)" value={manual.email} onChange={(e) => setManual({ ...manual, email: e.target.value })} style={{ padding: 6, border: "1px solid #c9d0dc", borderRadius: 6, width: 200 }} />
          <input placeholder="note (who / why)" value={manual.note} onChange={(e) => setManual({ ...manual, note: e.target.value })} style={{ padding: 6, border: "1px solid #c9d0dc", borderRadius: 6, width: 200 }} />
          <select value={manual.kind} onChange={(e) => setManual({ ...manual, kind: e.target.value })} style={{ padding: 6, border: "1px solid #c9d0dc", borderRadius: 6 }}>
            <option value="free">Free</option>
            <option value="discount">Discount</option>
          </select>
          {manual.kind === "discount" && (
            <input type="number" min={1} max={100} value={manual.discountPct} onChange={(e) => setManual({ ...manual, discountPct: e.target.value })} style={{ padding: 6, border: "1px solid #c9d0dc", borderRadius: 6, width: 70 }} />
          )}
          <button
            type="button"
            disabled={busy === "manual"}
            onClick={() => mintCode({ kind: manual.kind, discountPct: manual.discountPct, igHandle: manual.igHandle || null, email: manual.email || null, note: manual.note || null })}
            style={btn(true)}
          >
            Mint + copy DM
          </button>
        </div>
      </div>

      <h4 style={{ marginBottom: 6 }}>Codes issued ({codes.length})</h4>
      {codes.length === 0 ? (
        <div style={{ color: "#5b667a", fontSize: 14 }}>None yet.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#5b667a", fontSize: 12 }}>
                <th style={{ padding: 6 }}>Code</th>
                <th style={{ padding: 6 }}>Kind</th>
                <th style={{ padding: 6 }}>For</th>
                <th style={{ padding: 6 }}>Created</th>
                <th style={{ padding: 6 }}>Redeemed</th>
                <th style={{ padding: 6 }} />
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => {
                const used = c.uses >= c.maxUses;
                const expired = c.expiresAt && Date.parse(c.expiresAt) < Date.now();
                return (
                  <tr key={c.code} style={{ borderTop: "1px solid #eef1f6", opacity: used || expired ? 0.75 : 1 }}>
                    <td style={{ padding: 6, fontFamily: "monospace", fontWeight: 700, letterSpacing: "0.06em" }}>{c.code}</td>
                    <td style={{ padding: 6 }}>{c.kind === "free" ? "Free" : `${c.discountPct}% off`}</td>
                    <td style={{ padding: 6 }}>
                      {c.igHandle && <a href={`https://instagram.com/${c.igHandle}`} target="_blank" rel="noreferrer">@{c.igHandle}</a>}
                      {c.email && <div style={{ color: "#5b667a" }}>{c.email}</div>}
                      {c.note && <div style={{ color: "#8b94a6", fontSize: 12 }}>{c.note}</div>}
                    </td>
                    <td style={{ padding: 6, whiteSpace: "nowrap" }}>
                      {fmt(c.createdAt)}
                      {expired && <span style={chip("#f1f3f6", "#6a7385", "#d0d6e0")}>expired</span>}
                    </td>
                    <td style={{ padding: 6 }}>
                      {used ? (
                        <span style={chip("#e4f5e9", "#1f7a3a", "#a5d6a7")}>
                          ✓ {c.redeemedBy.join(", ") || "redeemed"}
                        </span>
                      ) : (
                        <span style={chip("#fff6de", "#8a5a00", "#e8c56b")}>unused</span>
                      )}
                      {c.lastRedeemedAt && <div style={{ fontSize: 11, color: "#5b667a" }}>{fmt(c.lastRedeemedAt)}</div>}
                    </td>
                    <td style={{ padding: 6, whiteSpace: "nowrap" }}>
                      <button type="button" onClick={() => copy(dmText(c), `code-${c.code}`)} style={btn(false)}>
                        {copied === `code-${c.code}` ? "Copied ✓" : "Copy DM"}
                      </button>
                      <button type="button" onClick={() => copy(c.link, `link-${c.code}`)} style={btn(false)}>
                        {copied === `link-${c.code}` ? "Copied ✓" : "Copy link"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvitesTab;
